import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { CommuneAutocomplete } from "@/components/CommuneAutocomplete";
import { Check, ShieldCheck } from "lucide-react";

export default function Accueil() {
  const [commune, setCommune] = useState(null);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState({ status: "idle", message: "" });

  const canSubmit = commune && email.includes("@") && consent && state.status !== "loading";

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setState({ status: "loading", message: "" });
    try {
      const r = await axios.post(`${API}/inscription`, {
        email,
        code_insee: commune.code_insee,
      });
      if (r.data.status === "code_insee_invalide") {
        setState({ status: "error", message: r.data.message });
      } else {
        setState({ status: "done", message: r.data.message });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setState({ status: "error", message: typeof detail === "string" ? detail : "Une erreur est survenue. Réessayez." });
    }
  };

  return (
    <div data-testid="screen-accueil">
      <h1 style={{ fontSize: "1.9rem", margin: "0.2rem 0 0.6rem" }}>
        Soyez prévenu si votre commune est reconnue en catastrophe naturelle
      </h1>
      <p style={{ color: "var(--muted)", marginTop: 0, fontSize: "1.02rem" }}>
        Indiquez votre commune et votre e-mail : dès qu'un arrêté de reconnaissance est publié au
        Journal officiel, vous recevez un message avec la date limite pour déclarer à votre assureur.
      </p>

      {state.status === "done" ? (
        <div className="pc-card" data-testid="inscription-success" style={{ marginTop: "1.2rem", background: "var(--accent-soft)", borderColor: "var(--accent)" }}>
          <div style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start" }}>
            <Check size={20} color="var(--accent)" style={{ marginTop: 2 }} />
            <p style={{ margin: 0 }}>{state.message}</p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="pc-card" style={{ marginTop: "1.2rem", display: "grid", gap: "0.9rem" }} data-testid="inscription-form">
          <div>
            <label style={{ fontWeight: 600, fontSize: "0.92rem", display: "block", marginBottom: "0.35rem" }}>Votre commune</label>
            <CommuneAutocomplete onSelect={setCommune} testId="accueil-commune" />
          </div>
          <div>
            <label style={{ fontWeight: 600, fontSize: "0.92rem", display: "block", marginBottom: "0.35rem" }}>Votre adresse e-mail</label>
            <input
              className="pc-input"
              type="email"
              data-testid="accueil-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.fr"
            />
          </div>
          <label style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start", fontSize: "0.9rem", color: "var(--muted)" }}>
            <input
              type="checkbox"
              data-testid="accueil-consent"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              style={{ marginTop: 3, width: 18, height: 18, accentColor: "var(--accent)" }}
            />
            <span>
              J'accepte de recevoir des notifications pour cette commune et j'ai lu la{" "}
              <Link to="/confidentialite" data-testid="accueil-privacy-link">politique de confidentialité</Link>.
            </span>
          </label>
          {state.status === "error" && (
            <p data-testid="inscription-error" style={{ color: "var(--warn)", margin: 0, fontSize: "0.9rem" }}>{state.message}</p>
          )}
          <button className="pc-btn" type="submit" data-testid="accueil-submit" disabled={!canSubmit}>
            {state.status === "loading" ? "Envoi…" : "Recevoir les notifications"}
          </button>
          <p style={{ fontSize: "0.82rem", color: "var(--muted)", margin: 0 }}>
            Vous recevrez un e-mail de confirmation. Tant que vous n'avez pas confirmé, aucune notification n'est envoyée.
          </p>
        </form>
      )}

      <div style={{ marginTop: "1.4rem", display: "flex", gap: "0.55rem", alignItems: "center", fontSize: "0.9rem", color: "var(--muted)" }}>
        <ShieldCheck size={18} color="var(--accent)" />
        <span>Service gratuit et indépendant — sans lien avec l'État, un assureur ou un courtier.</span>
      </div>

      <p style={{ marginTop: "1.4rem", fontSize: "0.95rem" }}>
        Vous voulez d'abord vérifier les arrêtés déjà publiés pour une commune ?{" "}
        <Link to="/recherche" data-testid="accueil-to-recherche">Rechercher une commune</Link>.
      </p>
    </div>
  );
}
